// What the Agents list draws, worked out from the chats (no React): the
// chats as groups (a top chat and the children under it), each row's state
// mark, the "active" filter, the order of the children and the sort by a
// column.
//
// Promise:
// - Side chats are left out; children (and grandchildren, right after their
//   parent) go under their top chat, oldest first. A child whose parent is not
//   in the list stands as a top chat.
// - A row's mark says what goes on there, one at a time: asking first (its
//   question is what needs the person, even while it goes on working), then
//   on a top chat waiting for its turn, then working, then "delegated to a
//   child" (a chat below it is working), then unread.
// - "Active" keeps the chats with a mark (and the open one), and a top chat
//   whose children have one; among the children only those (or those with a
//   marked chat below them). The others are hidden, not moved.
// - Past FOLD_AT children the moving ones come first: asking, then waiting
//   for their turn, then working, then the rest, each in the order they were
//   made. Only the moving ones stand above the fold, all of them however many
//   (none: the fold row right under the top chat); the rest is folded, also
//   five or fewer (the person 2026-11-02).
// - A top chat's "last change" is the newest of it and all its children: a
//   child that moves brings its top chat up.
// - Sorting by a column orders the groups (the secretary stays first);
//   rows with nothing in the column go last either way.
import type { AgentChatDTO } from "@trameria/core/agent-chat-shared";
import { foldOrder } from "../shell/list-fold";
import { chatTitle } from "./names";

export interface ListRow {
  chat: AgentChatDTO;
  depth: number;
}

/** Chats as rows: side chats out, children under their top chat. */
export function chatRows(chats: readonly AgentChatDTO[]): ListRow[] {
  const shown = chats.filter((c) => c.forkedFromEntryId === null);
  const ids = new Set(shown.map((c) => c.id));
  const kids = new Map<string, AgentChatDTO[]>();
  for (const c of shown)
    if (c.parentChatId && ids.has(c.parentChatId)) {
      const list = kids.get(c.parentChatId) ?? [];
      list.push(c);
      kids.set(c.parentChatId, list);
    }
  const out: ListRow[] = [];
  const walk = (c: AgentChatDTO, depth: number) => {
    out.push({ chat: c, depth });
    // The newest change first, as the list itself and its pages (the person
    // 2026-10-27): the children the pages have not brought yet are all older
    // than the ones they have, so the rows read later come after the rows
    // shown, and the fold opens the rest under itself without reordering.
    const children = (kids.get(c.id) ?? []).sort(
      (a, b) =>
        b.updatedAt.localeCompare(a.updatedAt) || b.id.localeCompare(a.id),
    );
    for (const k of children) walk(k, depth + 1);
  };
  for (const c of shown)
    if (!c.parentChatId || !ids.has(c.parentChatId)) walk(c, 0);
  return out;
}

export type ChatState =
  | "asking"
  | "queued"
  | "working"
  | "delegating"
  | "unread";

/**
 * A reply the owner has not read, as the list marks it. Not on a chat a
 * parent launched (not a Case's): the parent follows it and reads it (the
 * person 2026-10-27; the badge and the notices leave it out the same way).
 */
export const unread = (
  c: Pick<
    AgentChatDTO,
    "lastSeq" | "ownerReadSeq" | "launchedByParent" | "caseId" | "caseStepId"
  >,
) =>
  c.lastSeq > c.ownerReadSeq &&
  !(c.launchedByParent && !(c.caseId !== null && c.caseStepId === null));

/** The mark of a row (null: nothing goes on). `below`: the chats under it. */
export function chatState(
  chat: AgentChatDTO,
  below: readonly AgentChatDTO[],
  child: boolean,
): ChatState | null {
  // A question shows at once, also while the run goes on (the person 2026-10-19:
  // the old web's "working first" hid it until the run ended).
  if (chat.openItems > 0) return "asking";
  // A child does not show waiting for its turn (working is enough there).
  if (!child && chat.queued) return "queued";
  if (chat.running) return "working";
  if (below.some((c) => c.running)) return "delegating";
  if (unread(chat)) return "unread";
  return null;
}

export interface ListGroup {
  top: ListRow;
  /** The rows under the top chat, in the order drawn (moving ones first past FOLD_AT). */
  children: ListRow[];
  /** The newest change of the top chat and all its children. */
  latest: string;
  /**
   * How many of the children the list's pages read (they come first); the
   * rest were read for the opened fold. Absent: all of them.
   */
  read?: number;
}

export interface GroupOptions {
  active: boolean;
  /** The open chat: kept by "active" while it is open. */
  openKey: string | null;
}

/** The chats below each row of a group (depth first: until the depth comes back). */
export function belowOf(rows: readonly ListRow[], at: number): AgentChatDTO[] {
  const out: AgentChatDTO[] = [];
  const depth = rows[at].depth;
  for (let i = at + 1; i < rows.length && rows[i].depth > depth; i++)
    out.push(rows[i].chat);
  return out;
}

/** The chats below a chat (children, grandchildren …), by id, from a list. */
export function belowById(
  chats: readonly AgentChatDTO[],
): (id: string) => AgentChatDTO[] {
  const kids = new Map<string, AgentChatDTO[]>();
  for (const c of chats)
    if (c.parentChatId && c.forkedFromEntryId === null)
      kids.set(c.parentChatId, [...(kids.get(c.parentChatId) ?? []), c]);
  return (id) => {
    const out: AgentChatDTO[] = [];
    const seen = new Set([id]);
    const walk = (at: string) => {
      for (const k of kids.get(at) ?? [])
        if (!seen.has(k.id)) {
          seen.add(k.id);
          out.push(k);
          walk(k.id);
        }
    };
    walk(id);
    return out;
  };
}

const rank = (c: AgentChatDTO) =>
  c.openItems > 0 ? 0 : c.queued ? 1 : c.running ? 2 : 3;

export function chatGroups(
  rows: readonly ListRow[],
  { active, openKey }: GroupOptions,
): ListGroup[] {
  const groups: ListGroup[] = [];
  for (let i = 0; i < rows.length; ) {
    let end = i + 1;
    while (end < rows.length && rows[end].depth > 0) end++;
    const family = rows.slice(i, end);
    i = end;
    const marked = (c: AgentChatDTO) =>
      c.running !== null ||
      c.queued ||
      c.openItems > 0 ||
      unread(c) ||
      (openKey !== null && c.key === openKey);
    const stirring = (at: number) =>
      marked(family[at].chat) || belowOf(family, at).some(marked);
    if (active && !stirring(0)) continue;
    const children = foldOrder(
      family
        .map((row, at) => ({ row, at }))
        .slice(1)
        .filter(({ at }) => !active || stirring(at))
        .map(({ row }) => row),
      (row) => rank(row.chat),
    );
    const latest = family
      .map((r) => r.chat.updatedAt)
      .reduce((a, b) => (b > a ? b : a));
    groups.push({ top: family[0], children, latest });
  }
  return groups;
}

/**
 * The group with how many of its first rows the list's pages read, when the
 * opened fold read more after them (they come after: older): those stay above
 * the fold row, so opening it adds rows under it and moves nothing above it
 * (the person 2026-10-27: the first five changed between folded and open).
 */
export function withRead(
  group: ListGroup,
  paged: ReadonlySet<string>,
): ListGroup {
  const read = group.children.findIndex((r) => !paged.has(r.chat.id));
  return read < 0 ? group : { ...group, read };
}

/**
 * The children as the fold draws them, and how many stand above the fold row:
 * the moving ones the pages read, however many (also five or fewer: the
 * person 2026-11-02); the rows read for the opened fold stay under it
 * (opening it moves nothing above).
 */
export function foldRows(group: ListGroup): { rows: ListRow[]; head: number } {
  const read = group.read ?? group.children.length;
  const rows = group.children
    .map((row, at) => ({ row, at, k: (at < read ? 0 : 4) + rank(row.chat) }))
    .sort((a, b) => a.k - b.k || a.at - b.at)
    .map(({ row }) => row);
  const head = group.children
    .slice(0, read)
    .filter((r) => rank(r.chat) < 3).length;
  return { rows, head };
}

export type SortKey = "title" | "case" | "agent" | "cost" | "created" | "last";
export interface Sort {
  key: SortKey;
  dir: "asc" | "desc";
}
/** Newest change first (the old web's default, 2026-09-30). */
export const DEFAULT_SORT: Sort = { key: "last", dir: "desc" };

/**
 * The next sort when a column's head is pressed: ascending, then descending,
 * then back to the default. The default's own column turns to ascending.
 */
export function nextSort(current: Sort, key: SortKey): Sort {
  if (current.key !== key) return { key, dir: "asc" };
  if (current.dir === "asc") return { key, dir: "desc" };
  return key === DEFAULT_SORT.key ? { key, dir: "asc" } : DEFAULT_SORT;
}

export interface SortNames {
  caseName(id: string): string | undefined;
  agentName(chat: AgentChatDTO): string;
}

export function sortGroups(
  groups: readonly ListGroup[],
  sort: Sort,
  names: SortNames,
): ListGroup[] {
  const cellOf = (g: ListGroup): string | number | null => {
    const c = g.top.chat;
    switch (sort.key) {
      case "title":
        return chatTitle(c);
      case "case":
        return c.caseId ? (names.caseName(c.caseId) ?? null) : null;
      case "agent":
        return names.agentName(c) || null;
      case "cost":
        return c.costUsd > 0 ? c.costUsd : null;
      case "created":
        return c.createdAt;
      case "last":
        return g.latest;
    }
  };
  const dir = sort.dir === "asc" ? 1 : -1;
  const pinned = (g: ListGroup) => (g.top.chat.role === "secretary" ? 0 : 1);
  return groups
    .map((g, at) => ({ g, at, v: cellOf(g) }))
    .sort((a, b) => {
      const p = pinned(a.g) - pinned(b.g);
      if (p !== 0) return p;
      if (a.v === null || b.v === null)
        return a.v === b.v ? a.at - b.at : a.v === null ? 1 : -1;
      const c =
        typeof a.v === "number" && typeof b.v === "number"
          ? a.v - b.v
          : String(a.v).localeCompare(String(b.v), "ja");
      return c === 0 ? a.at - b.at : c * dir;
    })
    .map(({ g }) => g);
}
